import React from 'react';
import InfoTooltip from '../tooltip/InfoTooltip';
import { classNames } from '../utils/classNames';

// A chart legend in groups. A group has a label and a row of items; an item has a mark and a label.
// The mark is a dot, a square or a tint (a light fill with a bar at the bottom, as a day of MonthGrid),
// in the colour classes that the caller gives, or any node in place of the mark (an icon chip).
// A group can carry an (i) with an explanation, and a short note after its items.
// The Legend knows nothing about the data behind the colours: the caller gives labels and classes.
export type LegendMark = 'dot' | 'square' | 'tint';

export type LegendItem = {
	label: string,
	mark?: LegendMark,
	// The Tailwind classes of the mark: a fill (bg-blue-500), and a border colour for a tint.
	markClassName?: string,
	// A node that replaces the drawn mark, for example an icon chip.
	icon?: React.ReactNode,
};

export type LegendGroup = {
	label: string,
	items: LegendItem[],
	info?: { label: string, title: string, content: React.ReactNode },
	note?: string,
};

type Props = {
	groups: LegendGroup[],
};

const MARK: Record<LegendMark, string> = {
	dot: 'h-2 w-2 rounded-full',
	square: 'h-2.5 w-2.5 rounded-sm',
	tint: 'h-3 w-3 rounded-sm border-b-2',
};

const Legend: React.FunctionComponent<Props> = ({ groups }) =>
	<div className="flex flex-col gap-y-1.5">
		{groups.map(group => <div key={group.label} className="flex flex-wrap items-center gap-x-3 gap-y-1">
			<span className="inline-flex items-center gap-x-1 text-xs font-medium text-gray-500">
				{group.label}
				{group.info !== undefined && <InfoTooltip label={group.info.label} title={group.info.title}>{group.info.content}</InfoTooltip>}
			</span>
			{group.items.map(item => <span key={item.label} className="inline-flex items-center gap-x-1.5 text-xs text-gray-600">
				{item.icon ?? <span aria-hidden="true" className={classNames('inline-flex shrink-0', MARK[item.mark ?? 'dot'], item.markClassName ?? 'bg-gray-400')} />}
				{item.label}
			</span>)}
			{group.note !== undefined && <span className="text-xs text-gray-500">{group.note}</span>}
		</div>)}
	</div>;

export default Legend;
